import { useState, useEffect } from 'preact/hooks'
import { useLookups } from '@/hooks/useLookups'

interface CountryDropdownProps {
  value?: string
  onChange: (countryCode: string) => void
  error?: string
  disabled?: boolean
}

export function CountryDropdown({ value, onChange, error, disabled }: CountryDropdownProps) {
  const { getCountries } = useLookups()
  const [countrySearch, setCountrySearch] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)

  const filteredCountries = getCountries()
    .filter(c => 
      countrySearch === '' ||
      c.Description.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.Key.toLowerCase().includes(countrySearch.toLowerCase())
    )
    .sort((a, b) => a.Description.localeCompare(b.Description))

  // Initialize country search with current country name when value changes
  useEffect(() => {
    if (value) {
      const country = getCountries().find(c => c.Key === value)
      if (country) {
        setCountrySearch(country.Description)
      }
    } else {
      setCountrySearch('')
    }
  }, [value, getCountries])

  return (
    <div className="relative">
      <input
        type="text"
        value={countrySearch}
        onInput={(e) => {
          setCountrySearch((e.target as HTMLInputElement).value)
          setShowCountryDropdown(true)
        }}
        onFocus={() => setShowCountryDropdown(true)}
        onBlur={() => {
          // Delay to allow click on dropdown item
          setTimeout(() => setShowCountryDropdown(false), 200)
        }}
        className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${
          error 
            ? 'border-red-300 dark:border-red-600 focus:ring-red-500' 
            : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500 dark:focus:ring-primary-400'
        }`}
        placeholder="Type to search countries..."
        disabled={disabled}
        autoComplete="off"
      />
      {showCountryDropdown && filteredCountries.length > 0 && (
        <div className="absolute z-50 w-full bottom-full mb-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredCountries.slice(0, 50).map(c => (
            <button
              key={c.Key}
              type="button"
              onClick={() => {
                setCountrySearch(c.Description)
                onChange(c.Key)
                setShowCountryDropdown(false)
              }}
              className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 text-sm"
            >
              <span className="font-medium">{c.Description}</span>
              <span className="ml-2 text-slate-500 dark:text-slate-400 text-xs">({c.Key})</span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}
